<script lang="ts" setup>
import type { PropType} from 'vue';
import { ref, computed, onMounted, watch } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';

export type AgOptionSelectItem = {
    id: string;
    value: string | number;
    title: string | number;
    tooltip: string | null;
};

type ExtendedParams = ICellRendererParams & {
    items: AgOptionSelectItem[],
    selectedId: string | null | undefined,
    disabled: boolean,
    loading: boolean,
    readonly: boolean
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

/**********
 * Init
 **********/

const optionIdLoading = ref<string | null>(null);

const options = ref<AgOptionSelectItem[]>();
const readonly = computed(() => props.params.readonly);
const disabled = computed(() => props.params.disabled);
const selectedId = computed(() => props.params.selectedId);

const value = computed(() => (selectedId.value ?? props.params.value));

function click(option: AgOptionSelectItem): void {
    if (
        optionIdLoading.value != null
        || disabled.value === true
        || readonly.value === true
        || value.value === option.value
    ) return;
    if (props.params.setValue != null) {
        optionIdLoading.value = option.id;
        props.params.setValue(option.value);
    }
}

watch(() => props.params.value, (oldValue, newValue) => {
    if (oldValue !== newValue) {
        optionIdLoading.value = null;
    }
});

onMounted(() => {
    if (props.params.items == null) return;
    options.value = props.params.items;
});
</script>

<template>
    <div class="height-100 flex-container align-middle">
        <div class="sy-option-select-container small" :class="{'readonly' : readonly}" data-test="select-container">
            <label
                v-for="option of options"
                :key="option.value"
                v-tooltip="option.tooltip"
                :class="['sy-option-select-button small position-relative', { 'loading': optionIdLoading === option.id, 'active': value === option.id, 'readonly' : readonly, 'disabled': disabled } ]"
                :data-test="`factor-level-option-${option.title}`"
                @keypress.space="click(option)"
                @keypress.enter="click(option)"
                @click="click(option)"
            >
                <input
                    class="visually-hidden"
                    type="radio"
                    :name="option.value.toString()"
                />
                <div v-if="optionIdLoading === option.id" class="sy-ag-loading-icon">
                    <Icon
                        icon="loader-2"
                        class="sy-spin sy-icon-blue"
                    />
                </div>
                <span>{{ option.title }}</span>
            </label>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.sy-ag-loading-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 3;
    border-radius: inherit;
    background-color: var(--sy-blue-100);
}
</style>
